import React, { useEffect, useState } from 'react';
import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    SaveOutlined,
} from '@ant-design/icons';
import FileUploadField from '@components/common/form/FileUploadField';
import PageWrapper from '@components/common/layout/PageWrapper';
import { AppConstants } from '@constants';
import apiConfig from '@constants/apiConfig';
import useFetch from '@hooks/useFetch';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, BooleanField, SelectField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { Button, Card, Col, Flex, Form, Input, Modal, Row, Table, Upload } from 'antd';

export default function AppConfigurationPage() {
    const translate = useTranslate();
    const notification = useNotification();

    const [form] = Form.useForm();

    const [tableData, setTableData] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [dataDetail, setDataDetail] = useState({});
    const [componentOptions, setComponentOptions] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [paramsList, setParamsList] = useState([]);
    const [editingParamIndex, setEditingParamIndex] = useState(null);

    const [newKey, setNewKey] = useState('');
    const [editingKey, setEditingKey] = useState('');

    const { execute: executeUpload, loading: loadingUpload } = useFetch(apiConfig.file.uploadApk);
    const { execute: executeUploadExtra, loading: loadingUploadExtra } = useFetch(apiConfig.file.uploadApk);
    const { execute: executeCreateApp, loading: loadingCreateApp } = useFetch(apiConfig.systemSetting.create);
    const { execute: executeGetDetailApp, loading: loadingGetDetailApp } = useFetch(apiConfig.systemSetting.getDetail);
    const { execute: executeUpdateApp, loading: loadingUpdateApp } = useFetch(apiConfig.systemSetting.update);

    useEffect(() => {
        executeGetDetailApp({
            onCompleted: (res) => {
                if (res.result) {
                    if (res.data.id) {
                        setIsEditing(true);
                        setDataDetail(res.data);
                    }

                    const settingValueParsed = JSON.parse(res.data.settingValue);
                    const optionsParsed = JSON.parse(res.data.options);

                    form.setFieldsValue({
                        settingValue: settingValueParsed,
                        options: optionsParsed,
                    });

                    const bundle = settingValueParsed['android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE'];

                    if (bundle?.apps_to_install_json) {
                        const appsFromServer = JSON.parse(bundle.apps_to_install_json);

                        const mappedApps = appsFromServer.map((app, index) => {
                            let paramKeys = [];
                            if (app.params?.[0] && Object.keys(app.params[0]).length > 0) {
                                paramKeys = Object.keys(app.params[0]);
                            }

                            return {
                                key: Date.now() + index,
                                index: index + 1,
                                name: app.name,
                                package: app.package,
                                url: app.url,
                                params: paramKeys,
                                launcherActivity: app.launcherActivity,
                            };
                        });

                        setTableData(mappedApps);
                    }

                    setComponentOptions(
                        optionsParsed?.map((item) => ({
                            label: item,
                            value: item,
                        })) || [],
                    );
                }
            },
            onError: (error) => {
                console.log(error);
            },
        });
    }, []);

    const columns = [
        { title: '#', dataIndex: 'index', width: 60, align: 'center' },
        { title: translate.formatMessage(commonMessage.appName), dataIndex: 'name' },
        { title: translate.formatMessage(commonMessage.packageName), dataIndex: 'package' },
        { title: translate.formatMessage(commonMessage.launcher), dataIndex: 'launcherActivity' },

        { title: translate.formatMessage(commonMessage.url), dataIndex: 'url' },
        {
            title: translate.formatMessage(commonMessage.params),
            dataIndex: 'params',
            render: (params) => (params?.length ? params.map((key) => `{{${key}}}`).join(', ') : '-'),
        },
        {
            title: translate.formatMessage(commonMessage.action),
            width: 120,
            align: 'center',
            render: (_, record) => (
                <Flex gap={12} justify="center">
                    <EditOutlined style={{ cursor: 'pointer', color: '#1677ff' }} onClick={() => edit(record)} />
                    <DeleteOutlined
                        style={{ cursor: 'pointer', color: '#ff4d4f' }}
                        onClick={() => remove(record.key)}
                    />
                </Flex>
            ),
        },
    ];

    const startEditParam = (record, index) => {
        setEditingParamIndex(index);
        setEditingKey(record);
    };

    const edit = (record) => {
        setEditingRecord(record);
        setParamsList(record.params || []);
        setIsModalOpen(true);
    };

    const addParam = () => {
        const key = newKey.trim();

        if (!key) {
            notification({ type: 'error', message: translate.formatMessage(commonMessage.keyIsRequired) });
            return;
        }

        if (paramsList.includes(key)) {
            notification({ type: 'error', message: translate.formatMessage(commonMessage.duplicateKey) });
            return;
        }

        setParamsList([...paramsList, key]);
        setNewKey('');
    };

    const saveEditParam = (index) => {
        const key = editingKey.trim();

        if (!key) {
            notification({ type: 'error', message: translate.formatMessage(commonMessage.keyIsRequired) });
            return;
        }

        if (paramsList.some((k, i) => k === key && i !== index)) {
            notification({ type: 'error', message: translate.formatMessage(commonMessage.duplicateKey) });
            return;
        }

        const updated = [...paramsList];
        updated[index] = key;

        setParamsList(updated);
        setEditingParamIndex(null);
    };

    const cancelEditParam = () => {
        setEditingParamIndex(null);
    };

    const removeParam = (index) => {
        setParamsList(paramsList.filter((_, i) => i !== index));
    };

    const remove = (key) => {
        const newData = tableData.filter((item) => item.key !== key);

        // re-index lại
        const reIndex = newData.map((item, index) => ({
            ...item,
            index: index + 1,
        }));

        setTableData(reIndex);
    };

    const handleUpload = async (options) => {
        const { file, onSuccess, onError } = options;
        executeUpload({
            data: {
                type: 1,
                file: file,
            },
            onCompleted: (res) => {
                const responseData = res.data;
                const { componentNameList, ...settingValues } = responseData;

                if (res?.result) {
                    notification({ type: 'success', message: translate.formatMessage(commonMessage.uploadFileSuccessMessage) });

                    setComponentOptions(
                        componentNameList.map((item) => ({
                            label: item,
                            value: item,
                        })),
                    );

                    form.setFieldsValue({
                        settingValue: {
                            ...settingValues,
                            'android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION':
                                AppConstants.avatarRootUrl +
                                settingValues['android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION'],
                        },
                        options: componentNameList,
                    });

                    onSuccess('ok');
                } else {
                    notification({ type: 'error', message: res.data?.message || translate.formatMessage(commonMessage.uploadFailedMessage) });
                    onError();
                }
            },
            onError: (error) => {
                console.log(error);
                onError(error);
            },
        });
    };

    const handleUploadApp = async (options) => {
        const { file, onSuccess, onError } = options;

        executeUploadExtra({
            data: {
                type: 2,
                file: file,
            },
            onCompleted: (res) => {
                if (res?.result) {
                    const bundle = res.data['android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE'];

                    if (bundle?.apps_to_install_json) {
                        const appsFromServer = JSON.parse(bundle.apps_to_install_json);

                        const mappedApps = appsFromServer.map((app, index) => {
                            // Extract keys from params object if exists
                            let paramKeys = [];

                            if (app.params?.[0] && Object.keys(app.params[0]).length > 0) {
                                // Nếu có params từ server, lấy keys từ đó
                                paramKeys = Object.keys(app.params[0]);
                            } else {
                                // Tạo params default nếu không có
                                paramKeys = ['customerId', 'restaurantId'];
                            }

                            return {
                                key: Date.now() + index,
                                index: tableData.length + index + 1,
                                name: app.name,
                                package: app.package,
                                url: AppConstants.avatarRootUrl + app.url,
                                params: paramKeys,
                                launcherActivity: app.launcherActivity,
                            };
                        });

                        const newTableData = [...tableData, ...mappedApps];
                        setTableData(newTableData);
                    }

                    form.setFieldsValue({
                        'android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE': bundle,
                    });

                    notification({
                        type: 'success',
                        message: translate.formatMessage(commonMessage.addFileSuccess),
                    });

                    onSuccess('ok');
                } else {
                    notification({
                        type: 'error',
                        message: res?.message || translate.formatMessage(commonMessage.addFileFailed),
                    });
                    onError();
                }
            },
            onError: (error) => {
                onError(error);
            },
        });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingParamIndex(null);
        setEditingKey('');
        setNewKey('');
    };

    const handleSubmit = (values) => {
        const apps = tableData.map((item) => {
            const paramObject = item.params?.length
                ? item.params.reduce((acc, key) => {
                    acc[key] = `{{${key}}}`;
                    return acc;
                }, {})
                : {};

            return {
                url: item.url,
                name: item.name,
                package: item.package,
                params: Object.keys(paramObject).length > 0 ? [paramObject] : [],
                launcherActivity: item.launcherActivity,
            };
        });

        const bundle = {
            apps_to_install_json: JSON.stringify(apps),
        };

        const payload = {
            name: 'Setting DPC APP',
            description: 'Setting DPC APP',
            settingKey: 'dpc_setting',
            settingGroup: 'DPC',
            settingValue: JSON.stringify({
                ...values.settingValue,
                'android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE': bundle,
            }),
            options: JSON.stringify(values.options),
        };

        if (isEditing) {
            executeUpdateApp({
                data: {
                    ...payload,
                    id: dataDetail.id,
                },
                onCompleted: (res) => {
                    if (res.result) {
                        notification({ type: 'success', message: translate.formatMessage(commonMessage.updateAppConfigSuccess) });
                    }
                },
                onError: (error) => {
                    notification({ type: 'error', message: translate.formatMessage(commonMessage.updateAppConfigFailed) });
                },
            });
            return;
        }

        executeCreateApp({
            data: payload,
            onCompleted: (res) => {
                if (res.result) {
                    notification({ type: 'success', message: translate.formatMessage(commonMessage.createAppConfigSuccess) });
                }
            },
            onError: (error) => {
                notification({ type: 'error', message: translate.formatMessage(commonMessage.createAppConfigFailed) });
            },
        });
    };

    return (
        <PageWrapper
            routes={[
                {
                    breadcrumbName: translate.formatMessage(commonMessage.appConfiguration),
                },
            ]}
        >
            <div className="basic-form-page">
                <Card
                    title={
                        <Flex justify="space-between" align="center">
                            {translate.formatMessage(commonMessage.appConfiguration)}
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => form.submit()}
                                loading={loadingCreateApp || loadingUpdateApp}
                            >
                                {translate.formatMessage(commonMessage.save)}
                            </Button>
                        </Flex>
                    }
                    bordered={false}
                >
                    <BaseForm
                        labelCol={{ span: 10 }}
                        layout="horizontal"
                        labelWrap
                        labelAlign="left"
                        wrapperCol={{ flex: 1 }}
                        onFinish={handleSubmit}
                        form={form}
                    >
                        {/* UPLOAD APK */}
                        <Row gutter={16}>
                            <Col span={24}>
                                <FileUploadField
                                    loading={loadingUpload}
                                    label={translate.formatMessage(commonMessage.dpcApp)}
                                    accept=".apk"
                                    uploadFile={(file, onSuccess, onError) => {
                                        handleUpload({
                                            file,
                                            onSuccess,
                                            onError,
                                        });
                                    }}
                                />
                            </Col>
                        </Row>

                        {/* COMPONENT NAME */}
                        <Row gutter={16}>
                            <Col span={24}>
                                <SelectField
                                    required
                                    label={translate.formatMessage(commonMessage.componentName)}
                                    name={[
                                        'settingValue',
                                        'android.app.extra.PROVISIONING_DEVICE_ADMIN_COMPONENT_NAME',
                                    ]}
                                    options={componentOptions}
                                />
                            </Col>
                        </Row>

                        {/* SIGNATURE */}
                        <Row gutter={16}>
                            <Col span={24}>
                                <TextField
                                    required
                                    label={translate.formatMessage(commonMessage.signature)}
                                    disabled
                                    name={[
                                        'settingValue',
                                        'android.app.extra.PROVISIONING_DEVICE_ADMIN_SIGNATURE_CHECKSUM',
                                    ]}
                                />
                            </Col>
                        </Row>

                        {/* BOOLEAN FIELDS */}
                        <Row gutter={16}>
                            <Col span={24}>
                                <BooleanField
                                    required
                                    label={translate.formatMessage(commonMessage.provisioningSkipEncryption)}
                                    name={['settingValue', 'android.app.extra.PROVISIONING_SKIP_ENCRYPTION']}
                                />
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={24}>
                                <BooleanField
                                    required
                                    label={translate.formatMessage(commonMessage.provisioningLeaveAllSystemEnabled)}
                                    name={[
                                        'settingValue',
                                        'android.app.extra.PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED',
                                    ]}
                                />
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item label={translate.formatMessage(commonMessage.params)}>
                                    <Form.Item shouldUpdate noStyle>
                                        {({ getFieldValue }) => {
                                            const ssid = getFieldValue([
                                                'settingValue',
                                                'android.app.extra.PROVISIONING_WIFI_SSID',
                                            ]);
                                            const pass = getFieldValue([
                                                'settingValue',
                                                'android.app.extra.PROVISIONING_WIFI_PASSWORD',
                                            ]);

                                            return (
                                                <Row gutter={8}>
                                                    <div>{ssid}</div>
                                                    {pass ? ', ' : ''}
                                                    <div>{pass}</div>
                                                </Row>
                                            );
                                        }}
                                    </Form.Item>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name={['settingValue', 'android.app.extra.PROVISIONING_WIFI_SSID']} hidden>
                            <Input />
                        </Form.Item>

                        <Form.Item name={['settingValue', 'android.app.extra.PROVISIONING_WIFI_PASSWORD']} hidden>
                            <Input />
                        </Form.Item>

                        <Form.Item name={['settingValue', 'android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE']} hidden>
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name={[
                                'settingValue',
                                'android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION',
                            ]}
                            hidden
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item name={'options'} hidden>
                            <Input />
                        </Form.Item>
                    </BaseForm>

                    {/* TABLE */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
                                <div />
                                <Upload customRequest={handleUploadApp} accept=".apk" showUploadList={false}>
                                    <Button type="dashed" icon={<PlusOutlined />} loading={loadingUploadExtra}>
                                        {translate.formatMessage(commonMessage.addExtra)}
                                    </Button>
                                </Upload>
                            </Flex>
                            <Table columns={columns} dataSource={tableData} pagination={false} bordered />
                        </Col>
                    </Row>
                </Card>
            </div>

            <Modal
                title={translate.formatMessage(commonMessage.editParams)}
                open={isModalOpen}
                footer={null}
                onCancel={closeModal}
                width={500}
            >
                {/* Add Row */}
                <Row gutter={12} align="middle" style={{ marginBottom: 16 }}>
                    <Col span={20}>
                        <Input
                            placeholder={translate.formatMessage(commonMessage.placeholderKey)}
                            value={newKey}
                            onChange={(e) => setNewKey(e.target.value)}
                            onPressEnter={addParam}
                        />
                    </Col>
                    <Col span={4}>
                        <Button type="primary" onClick={addParam}>
                            {translate.formatMessage(commonMessage.addExtra)}
                        </Button>
                    </Col>
                </Row>

                {/* Table */}
                <Table
                    pagination={false}
                    bordered
                    rowKey={(record, index) => index}
                    dataSource={paramsList}
                    columns={[
                        {
                            title: translate.formatMessage(commonMessage.key),
                            dataIndex: '',
                            render: (text, record, index) =>
                                editingParamIndex === index ? (
                                    <Input value={editingKey} onChange={(e) => setEditingKey(e.target.value)} />
                                ) : (
                                    record
                                ),
                        },
                        {
                            title: translate.formatMessage(commonMessage.action),
                            width: 100,
                            render: (_, record, index) =>
                                editingParamIndex === index ? (
                                    <Flex gap={12}>
                                        <CheckOutlined
                                            style={{ color: '#52c41a', cursor: 'pointer' }}
                                            onClick={() => saveEditParam(index)}
                                        />
                                        <CloseOutlined
                                            style={{ color: '#ff4d4f', cursor: 'pointer' }}
                                            onClick={cancelEditParam}
                                        />
                                    </Flex>
                                ) : (
                                    <Flex gap={12}>
                                        <EditOutlined
                                            style={{ cursor: 'pointer', color: '#1677ff' }}
                                            onClick={() => startEditParam(record, index)}
                                        />
                                        <DeleteOutlined
                                            style={{ cursor: 'pointer', color: '#ff4d4f' }}
                                            onClick={() => removeParam(index)}
                                        />
                                    </Flex>
                                ),
                        },
                    ]}
                />

                <Flex justify="end" style={{ marginTop: 16 }}>
                    <Button
                        type="primary"
                        onClick={() => {
                            const newData = [...tableData];
                            const idx = newData.findIndex((item) => item.key === editingRecord?.key);

                            if (idx > -1) {
                                newData[idx].params = paramsList; // Store as array of keys
                                setTableData(newData);
                            }

                            setIsModalOpen(false);
                        }}
                    >
                        {translate.formatMessage(commonMessage.save)}
                    </Button>
                </Flex>
            </Modal>
        </PageWrapper>
    );
}
